import { useEffect, useRef, useState } from 'react';
import { builds, films, projects } from './data';
import { IconArrow } from './Icons';
import LazyImg from './LazyImg';
import Reveal from './Reveal';
import SectionHead from './SectionHead';

function FilmStage({ src, title }) {
  const box = useRef(null);
  const [near, setNear] = useState(false);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setNear(true);
        observer.disconnect();
      },
      { rootMargin: '320px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="film__stage" ref={box}>
      <div className={`film__plate${live ? ' is-hidden' : ''}`}>
        <span className="film__play" aria-hidden="true" />
        <em>{title}</em>
      </div>
      {near ? (
        <video
          className={live ? 'is-on' : ''}
          controls
          playsInline
          preload="metadata"
          aria-label={title}
          onLoadedData={() => setLive(true)}
          onError={() => setLive(false)}
        >
          <source src={src} type="video/mp4" />
        </video>
      ) : null}
    </div>
  );
}

export default function Work() {
  return (
    <section className="section" id="work">
      <div className="wrap">
        <SectionHead
          index="01"
          label="Work"
          title="Road vision, then the other systems."
          lede="Two computer-vision films from the current work. The rest of the last two years is underneath."
        />
        <div className="films">
          {films.map((film, index) => (
            <Reveal as="article" className={`film${index % 2 ? ' film--flip' : ''}`} key={film.id} id={film.id}>
              <FilmStage src={`${process.env.PUBLIC_URL}/videos/${film.file}`} title={film.title} />
              <div className="film__copy">
                <p className="case__meta">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {film.kicker}
                </p>
                <h3>{film.title}</h3>
                <p className="film__place">{film.place}</p>
                <p className="case__text">{film.text}</p>
                <ul className="chips" aria-label="What the film covers">
                  {film.tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="builds-head">
          <Reveal>
            <h3 className="subhead">Also in these two years</h3>
          </Reveal>
        </div>
        <div className="others">
          {projects.map((project) => (
            <Reveal as="article" className="other" key={project.id} id={project.id}>
              <p className="other__meta">{project.meta}</p>
              <h3>{project.title}</h3>
              <p>{project.summary}</p>
              <ul className="chips">
                {project.facts.map((fact) => (
                  <li key={fact.k}>
                    {fact.v} {fact.k}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>

        <div className="builds-head">
          <Reveal>
            <h3 className="subhead">Earlier, still public</h3>
            <p className="lede">College and side projects on GitHub.</p>
          </Reveal>
        </div>
        <div className="builds">
          {builds.map((build, index) => (
            <Reveal key={build.id} delay={(index % 3) * 70}>
              <a className="build" href={build.href} target="_blank" rel="noreferrer noopener">
                <div className="build__img">
                  <LazyImg src={build.image} alt="" />
                </div>
                <div className="build__body">
                  <p className="build__tag">{build.tag}</p>
                  <h3>{build.title}</h3>
                  <p>{build.summary}</p>
                  <span className="textlink">
                    GitHub <IconArrow size={14} />
                  </span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
